import { useState, useCallback } from 'react';
import { PDFManipulator } from '../../core/PDFManipulator';
import { readFileAsArrayBuffer, downloadUint8Array } from '../../utils/fileUtils';
import { useUIStore } from '../../stores/uiStore';
import './Dialogs.css';

interface MergeDialogProps {
  onClose: () => void;
}

export function MergeDialog({ onClose }: MergeDialogProps) {
  const [files, setFiles] = useState<{ name: string; data: ArrayBuffer }[]>([]);
  const [isMerging, setIsMerging] = useState(false);
  const [dragIdx, setDragIdx] = useState<number | null>(null);

  const handleAddFiles = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const newFiles = e.target.files;
    if (!newFiles) return;

    const loaded: { name: string; data: ArrayBuffer }[] = [];
    for (const file of Array.from(newFiles)) {
      if (file.type === 'application/pdf') {
        const data = await readFileAsArrayBuffer(file);
        loaded.push({ name: file.name, data });
      }
    }
    setFiles((prev) => [...prev, ...loaded]);
    e.target.value = '';
  }, []);

  const handleRemoveFile = useCallback((index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleMoveUp = useCallback((index: number) => {
    if (index === 0) return;
    setFiles((prev) => {
      const newFiles = [...prev];
      [newFiles[index - 1], newFiles[index]] = [newFiles[index], newFiles[index - 1]];
      return newFiles;
    });
  }, []);

  const handleMoveDown = useCallback((index: number) => {
    setFiles((prev) => {
      if (index === prev.length - 1) return prev;
      const newFiles = [...prev];
      [newFiles[index], newFiles[index + 1]] = [newFiles[index + 1], newFiles[index]];
      return newFiles;
    });
  }, []);

  const handleMerge = useCallback(async () => {
    if (files.length < 2) {
      useUIStore.getState().addToast({ type: 'warning', message: 'Add at least 2 PDF files to merge' });
      return;
    }

    setIsMerging(true);
    try {
      const result = await PDFManipulator.merge({ files: files.map((f) => f.data) });
      downloadUint8Array(result, 'merged.pdf');
      useUIStore.getState().addToast({ type: 'success', message: 'PDFs merged successfully!' });
      onClose();
    } catch (err) {
      console.error('Merge failed:', err);
      useUIStore.getState().addToast({ type: 'error', message: 'Failed to merge PDFs' });
    } finally {
      setIsMerging(false);
    }
  }, [files, onClose]);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ width: 520 }}>
        <div className="modal-header">
          <h3>📎 Merge PDFs</h3>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          <p style={{ marginBottom: 'var(--space-4)', color: 'var(--color-text-secondary)', fontSize: 'var(--text-sm)' }}>
            Select multiple PDF files and arrange their order. They will be combined into a single document.
          </p>

          <label className="file-drop-zone">
            <input
              type="file"
              accept=".pdf"
              multiple
              onChange={handleAddFiles}
              className="sr-only"
            />
            <span className="file-drop-icon">📂</span>
            <span className="file-drop-label">Click to add PDF files</span>
            <span className="file-drop-hint">or drag & drop files here</span>
          </label>

          {files.length > 0 && (
            <div className="merge-file-list">
              {files.map((file, index) => (
                <div key={index} className="merge-file-item">
                  <span className="merge-file-index">{index + 1}</span>
                  <span className="merge-file-name truncate">{file.name}</span>
                  <div className="merge-file-actions">
                    <button className="btn btn-ghost btn-icon btn-sm" onClick={() => handleMoveUp(index)} disabled={index === 0} title="Move up">
                      ↑
                    </button>
                    <button className="btn btn-ghost btn-icon btn-sm" onClick={() => handleMoveDown(index)} disabled={index === files.length - 1} title="Move down">
                      ↓
                    </button>
                    <button className="btn btn-ghost btn-icon btn-sm" onClick={() => handleRemoveFile(index)} title="Remove">
                      ×
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button
            className="btn btn-primary"
            onClick={handleMerge}
            disabled={files.length < 2 || isMerging}
          >
            {isMerging ? '⏳ Merging...' : `Merge ${files.length} Files`}
          </button>
        </div>
      </div>
    </div>
  );
}
